<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessageBox } from 'element-plus';
import { useAuthStore } from '@/stores/auth';
import { hasAny } from '@/utils/permissions';
import type { RoleCode } from '@/types';

interface MenuItem {
  path: string;
  title: string;
  roles?: RoleCode[];
  group: string;
  hidden?: boolean;
}

const auth = useAuthStore();
const router = useRouter();
const route = useRoute();

const menus: MenuItem[] = [
  { path: '/dashboard', title: '总部大盘', group: '总览' },

  { path: '/accounts', title: '账号管理', roles: ['super_admin'], group: '组织' },
  { path: '/roles', title: '角色权限', roles: ['super_admin'], group: '组织' },

  { path: '/stores/applications', title: '入驻审核', roles: ['super_admin', 'hq_ops'], group: '门店' },
  { path: '/stores/applications/new', title: '代录申请', roles: ['super_admin', 'hq_ops'], group: '门店' },
  { path: '/stores', title: '门店列表', roles: ['super_admin', 'hq_ops'], group: '门店' },

  { path: '/categories', title: '商品分类', roles: ['super_admin', 'hq_ops'], group: '商品' },
  { path: '/products', title: '实物商品', roles: ['super_admin', 'hq_ops'], group: '商品' },
  { path: '/service-packages', title: '服务套餐', roles: ['super_admin', 'hq_ops'], group: '商品' },
  { path: '/pricing-tiers', title: '出货价矩阵', roles: ['super_admin', 'hq_ops'], group: '商品' },
  { path: '/replenish/orders', title: '补货单管理', roles: ['super_admin', 'hq_ops'], group: '商品' },

  { path: '/mall/dashboard', title: '商城概览', roles: ['super_admin', 'hq_ops'], group: '商城' },
  { path: '/mall/listings', title: '商品上架', roles: ['super_admin', 'hq_ops'], group: '商城' },
  { path: '/mall/orders', title: '商城订单', roles: ['super_admin', 'hq_ops', 'hq_cs'], group: '商城' },
  { path: '/mall/shipments', title: '批量发货', roles: ['super_admin', 'hq_ops'], group: '商城' },
  { path: '/mall/after-sales', title: '退款售后', roles: ['super_admin', 'hq_ops', 'hq_cs'], group: '商城' },
  { path: '/mall/shipping-templates', title: '运费模板', roles: ['super_admin', 'hq_ops'], group: '商城' },
  { path: '/mall/reports', title: '商城报表', roles: ['super_admin', 'hq_ops', 'hq_finance'], group: '商城' },

  { path: '/content/articles', title: '馥之道文章', roles: ['super_admin', 'hq_admin', 'hq_ops'], group: '内容运营' },

  { path: '/devices', title: '设备总览', roles: ['super_admin', 'hq_ops'], group: '设备' },
  { path: '/devices/firmwares', title: 'OTA 固件', roles: ['super_admin', 'hq_ops'], group: '设备' },
  { path: '/devices/faults', title: '故障工单', roles: ['super_admin', 'hq_ops', 'hq_cs'], group: '设备' },

  { path: '/finance/settlements', title: '跨店结算', roles: ['super_admin', 'hq_finance'], group: '财务' },
  { path: '/finance/withdrawals', title: '提现审核', roles: ['super_admin', 'hq_finance'], group: '财务' },
  { path: '/finance/commission-rules', title: '分润规则', roles: ['super_admin', 'hq_finance'], group: '财务' },
  { path: '/finance/reconcile', title: '分润对账', roles: ['super_admin', 'hq_finance'], group: '财务' },
  { path: '/finance/reports', title: '财务报表', roles: ['super_admin', 'hq_finance'], group: '财务' },

  { path: '/referral/rules', title: '推荐归属', roles: ['super_admin', 'hq_ops'], group: '推荐' },
  { path: '/referral/relations', title: '推荐关系', roles: ['super_admin', 'hq_ops'], group: '推荐' },
  { path: '/referral/commissions', title: '分润流水', roles: ['super_admin', 'hq_finance', 'hq_ops'], group: '推荐' },
  { path: '/referral/payouts', title: '提现审核', roles: ['super_admin', 'hq_finance'], group: '推荐' },

  { path: '/system/configs', title: '全局参数', roles: ['super_admin'], group: '系统' },
  { path: '/system/templates/wx', title: '微信模板', roles: ['super_admin', 'hq_ops'], group: '系统' },
  { path: '/system/templates/sms', title: '短信模板', roles: ['super_admin', 'hq_ops'], group: '系统' },
  { path: '/system/agreements', title: '协议文本', roles: ['super_admin'], group: '系统' },
  { path: '/system/audit', title: '审计日志', roles: ['super_admin'], group: '系统' },
  { path: '/system/health', title: '系统状态', roles: ['super_admin'], group: '系统' },
  { path: '/system/service-catalog', title: '服务项目', roles: ['super_admin', 'hq_admin'], group: '系统' },
];

const visibleMenus = computed(() =>
  menus.filter((m) => !m.hidden && (!m.roles || hasAny(auth.roles, m.roles))),
);

const groupedMenus = computed(() => {
  const groups = new Map<string, MenuItem[]>();
  for (const item of visibleMenus.value) {
    const arr = groups.get(item.group) ?? [];
    arr.push(item);
    groups.set(item.group, arr);
  }
  return Array.from(groups.entries()).map(([title, items]) => ({ title, items }));
});

const activePath = computed(() => route.path);

async function handleLogout(): Promise<void> {
  try {
    await ElMessageBox.confirm('确认退出登录？', '提示', { type: 'warning' });
  } catch {
    return;
  }
  await auth.logout();
  router.push({ name: 'login' });
}
</script>

<template>
  <el-container class="layout-root">
    <el-aside width="220px" class="side">
      <div class="brand">
        <div class="brand-name">丝馥生</div>
        <div class="brand-sub">总部管理</div>
      </div>
      <el-menu
        :default-active="activePath"
        router
        background-color="#0f2a24"
        text-color="#dfd6c0"
        active-text-color="#d4b888"
      >
        <template v-for="group in groupedMenus" :key="group.title">
          <div class="menu-group">{{ group.title }}</div>
          <el-menu-item v-for="item in group.items" :key="item.path" :index="item.path">
            <span>{{ item.title }}</span>
          </el-menu-item>
        </template>
      </el-menu>
    </el-aside>
    <el-container>
      <el-header class="topbar">
        <div class="brand-meta">总部品牌方管理后台</div>
        <div class="user-info">
          <span class="hello">{{ auth.displayName }}</span>
          <el-tag v-for="role in auth.roles" :key="role" size="small" type="info">{{ role }}</el-tag>
          <el-dropdown>
            <el-button size="small" type="primary" link>账户</el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="router.push('/system/audit')">我的操作</el-dropdown-item>
                <el-dropdown-item divided @click="handleLogout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>
      <el-main class="main">
        <slot />
      </el-main>
    </el-container>
  </el-container>
</template>

<style scoped>
.layout-root {
  height: 100vh;
}

.side {
  background: #0f2a24;
  color: #dfd6c0;
  height: 100vh;
  overflow-y: auto;
}

.brand {
  padding: 18px 20px 20px;
  border-bottom: 1px solid #1a3a32;
}

.brand-name {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.18em;
  color: #d4b888;
}

.brand-sub {
  font-size: 12px;
  color: #7b938b;
  margin-top: 4px;
}

.menu-group {
  font-size: 11px;
  letter-spacing: 0.18em;
  padding: 12px 20px 6px;
  color: #7b938b;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-bottom: 1px solid #ede7d8;
}

.brand-meta {
  font-size: 14px;
  letter-spacing: 0.18em;
  color: #486860;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hello {
  font-size: 14px;
  color: #486860;
}

.main {
  background: #fbf8f1;
  padding: 20px 24px;
}
</style>
